import React, { useState, useEffect } from 'react';
import { history, useSelector } from 'umi';
import { useMemoizedFn } from 'ahooks';
// import { Card } from 'tea-component';
import { PagingQuery } from 'tea-component';
import GlobalCompareFilter, {
  convertRegionTypeByMarkets,
} from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/components/GlobalCompareFilter';
import GlobalCompareTable from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/components/GlobalCompareTable';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { fetchMobileStoreRankTopGlobalCompare } from '@/pages/intelligence/services/storeRank';
import { getDetailGamePathCommon } from '@/pages/intelligence/utils/topchartsUtils';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import {
  handleSingleDate2RangeRangeTimeInFilterExtend,
  handleRangeDate2SingleDateRangeTimeFilterExtend,
} from '@intelligence/utils/dateUtils';
import { getInitialFetchParams } from './utils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import { MOBILE_SOURCES } from '@/pages/intelligence/_config/storeRank/config';
import './index.scss';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IMobileStoreRankTopGlobalCompareFilter,
  IMobileStoreRankTopGlobalCompareFetchParams,
  IMobileStoreRankTopGlobalCompareRank,
  IMobileStoreRankTopGlobalCompareRankGame,
} from '@/pages/intelligence/types/storeRank';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  saveFilterValueStorage,
  saveFilterName,
  getFilterName,
  getFilterValueStorage,
} from '@/pages/intelligence/components/Filter/util';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import { analysisFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';

const DEFAULT_FETCH_PARAMS = {
  source: MOBILE_SOURCES[0],
  region_type: 'region',
  regions: ['global'],
  genre: [],
  sub_genre: [],
  genre_source: 'sensortower',
  iegg_genre_req: [],
  date: '',
  page: 1,
  page_size: 20,
};

type IGlobalCompareProps = IBaseProps & {
  condKey?: string;
  pageLogConfigKey?: string;
};

const GlobalCompare: React.FC<IGlobalCompareProps> = (props) => {
  const { condKey, pageLogConfigKey } = props;

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.storerankMobileGlobalCompare?.type,
    rangeTimeType: FilterTimeType.sameDay,
  });
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 在筛选器这里拿到缓存的值，并渲染
  const filterName = getFilterName(userInfo);
  const filterData = getFilterValueStorage(userInfo);

  const filterState = useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]);
  const isOperate = filterState?.isOperate ?? false;

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  const [fetchParams, setFetchParams] =
    useState<IMobileStoreRankTopGlobalCompareFetchParams>(DEFAULT_FETCH_PARAMS);
  const [initialFilter, setInitialFilter] = useState<any>(DEFAULT_FETCH_PARAMS);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [compareRanksTotal, setCompareRanksTotal] = useState(0);
  const [compareRanks, setCompareRanks] = useState<IMobileStoreRankTopGlobalCompareRank[]>([]);

  const handleGenreParams = (
    payload: IMobileStoreRankTopGlobalCompareFetchParams,
  ): Partial<IMobileStoreRankTopGlobalCompareFetchParams> => {
    if (!payload) return {};
    const params: Partial<IMobileStoreRankTopGlobalCompareFetchParams> = { ...payload };
    if (params?.genre_source === 'iegg') {
      delete params.genre;
      delete params.sub_genre;
    } else {
      delete params.genre_source;
      delete params.iegg_genre_req;
    }
    return params;
  };

  const updateCompareData = async (
    payload: Partial<IMobileStoreRankTopGlobalCompareFetchParams>,
  ) => {
    setIsLoading(true);
    setIsError(false);
    try {
      let fetchParamsTmp: any = {
        ...fetchParams,
        ...(payload ?? {}),
        region_type: convertRegionTypeByMarkets(payload?.regions as string[]),
      };
      fetchParamsTmp = handleGenreParams(fetchParamsTmp);
      const res = await fetchMobileStoreRankTopGlobalCompare(fetchParamsTmp);
      const resData = res?.data ?? {};
      const reqData = res?.req ?? {};
      const reqDate = reqData?.date ?? '';
      const compareRanksTotalTmp = resData?.total ?? 0;
      const compareRanksTmp = resData?.rank_list ?? [];
      if (reqDate) {
        fetchParamsTmp = {
          ...fetchParamsTmp,
          date: reqDate,
        };
      }
      setFetchParams(fetchParamsTmp);
      setCompareRanksTotal(compareRanksTotalTmp);
      setCompareRanks(compareRanksTmp);
    } catch (exception) {
      console.error(exception);
      setIsError(true);
      setCompareRanksTotal(0);
      setCompareRanks([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterConfirm = (payload: IMobileStoreRankTopGlobalCompareFilter) => {
    const fetchParamsTmp = {
      ...fetchParams,
      ...payload,
      page: 1,
      page_size: 20,
    };
    updateURLSearchParams({ source: fetchParamsTmp?.source });
    updateCompareData(fetchParamsTmp);
    setFetchParams(fetchParamsTmp);
  };

  const handleFilterReset = () => {};

  const handlePagingChange = (payload: PagingQuery) => {
    const fetchParamsTmp = {
      ...fetchParams,
      page: payload?.pageIndex ?? fetchParams?.page,
      page_size: payload?.pageSize ?? fetchParams?.page_size,
    };
    updateCompareData(fetchParamsTmp);
    setFetchParams(fetchParamsTmp);
  };

  const handleRefresh = () => {
    updateCompareData(fetchParams);
  };

  const getCountryDetailPath = () => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('source', encodeURIComponent(fetchParams?.source));
    const mobileStoreRankTopMorePagePath = `/intelligence/storeRanks/MobileGlobalRegion/more/globalcompare?${urlSearchParams.toString()}`;
    return mobileStoreRankTopMorePagePath;
  };

  const handleCountryClick = useMemoizedFn((countryCode: string) => {
    /** 点击列表的国家和地区后，继承单国家到下钻
     * 1清空保存方案
     * 2把国家添加到继承里
     */
    saveFilterName(userInfo, ''); // 清除缓存
    saveFilterValueStorage(userInfo, null);
    setFilterExtend({
      [FILTER_KEYS.COMPOSED_COUNTRY_SELECT]: {
        value: {
          type: 'single',
          countries: [countryCode],
        },
      },
    });
    history.push(getCountryDetailPath());
  });

  const getDetailGamePath = (game: IMobileStoreRankTopGlobalCompareRankGame) => {
    const gameId = game?.unified_id;
    if (!gameId) return '';
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('source', encodeURIComponent(fetchParams?.source));
    const mobileGameDetailsPagePath = getDetailGamePathCommon({
      entityType: 'mobile',
      gameId,
      tab: 'storeRank',
      subTab: '',
    });
    return `${mobileGameDetailsPagePath}?${urlSearchParams.toString()}`;
  };

  const handleGameClick = useMemoizedFn((game: IMobileStoreRankTopGlobalCompareRankGame) => {
    const gameId = game?.unified_id;
    if (!gameId) return;
    if (isOperate) {
      const filterExtendTmp = handleSingleDate2RangeRangeTimeInFilterExtend(filterExtend);
      setFilterExtend(filterExtendTmp);
    }
    history.push(getDetailGamePath(game));
  });

  useEffect(() => {
    if (!userInfo?.userid) {
      // 拿缓存值需要拿到用户id
      return;
    }

    const initFilter = async () => {
      let newFetchParams = { ...DEFAULT_FETCH_PARAMS };

      // 优先级：1. 用户手动选择的参数（filterExtend） 2. 后端默认值（condKey） 3. 保存的筛选方案
      if (filterState.isOperate === true) {
        /** 优先获取用户手动选择的继承参数 */
        const extendParams = getExtendParams();
        if (extendParams && Object.keys(extendParams).length > 0) {
          newFetchParams = { ...newFetchParams, ...extendParams };
        } else {
          // 如果继承参数为空，使用后端默认值
          const filterTagName = condKey || (history.location.query?.filter_tag_name as string);
          if (filterTagName) {
            const urlFilterParams = await analysisFilterParams(filterTagName);
            if (urlFilterParams && Object.keys(urlFilterParams).length > 0) {
              newFetchParams = { ...newFetchParams, ...urlFilterParams };
            }
          }
        }
      } else {
        // 用户未手动选择，使用后端默认值
        const filterTagName = condKey || (history.location.query?.filter_tag_name as string);
        if (filterTagName) {
          const urlFilterParams = await analysisFilterParams(filterTagName);
          if (urlFilterParams && Object.keys(urlFilterParams).length > 0) {
            newFetchParams = { ...newFetchParams, ...urlFilterParams };
          }
        } else if (filterName) {
          // 最后使用保存的筛选方案
          const filterSaveParams = getFilterSaveParams();
          newFetchParams = { ...newFetchParams, ...filterSaveParams };
        }
      }

      if(!newFetchParams.date){
        const today = new Date().toISOString().split('T')[0];
        newFetchParams.date = today;
      }
      setInitialFilter(newFetchParams);
      updateURLSearchParams({ source: newFetchParams?.source });
      updateCompareData(newFetchParams);
      const pageLogConfigKeyTmp = pageLogConfigKey || 'mobileStoreRankGlobalCompare';
      buriedPointPage(dbiPageLogMes[pageLogConfigKeyTmp]);
    };

    initFilter();
  }, [userInfo?.userid, condKey, pageLogConfigKey]);

  const getExtendParams = () => {
    const filterExtendTmp = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtend);
    return handleFilterParams(filterExtendTmp);
  };

  const getFilterSaveParams = () => {
    return handleFilterParams(filterData?.apiValue);
  };

  const handleFilterParams = (params: any) => {
    return getInitialFetchParams(params, DEFAULT_FETCH_PARAMS);
  };

  return (
    <>
      <GlobalCompareFilter
        isOperate={isOperate}
        granularity={'daily'}
        startDate={fetchParams?.date}
        endDate={fetchParams?.date}
        filter={initialFilter}
        onConfirm={handleFilterConfirm}
        onReset={handleFilterReset}
        rangeTimeComponentDefault={latestTimeTypeHasData}
        entityType="mobile"
      />

      <CardSlot>
        <GlobalCompareTable
          isLoading={isLoading}
          isError={isError}
          total={compareRanksTotal}
          records={compareRanks}
          pageIndex={fetchParams.page}
          pageSize={fetchParams.page_size}
          onPagingChange={handlePagingChange}
          onRefresh={handleRefresh}
          onCountryClick={handleCountryClick}
          onGameClick={handleGameClick}
          getCountryDetailPath={getCountryDetailPath}
          getDetailGamePath={getDetailGamePath}
        />
      </CardSlot>
    </>
  );
};

export default GlobalCompare;
